import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { getListLayout, getVisibleRange } from './layout';
import css from './VirtualizedList.scss';

type Props<T> = {
	className?: string;
	items: readonly T[];
	itemHeight?: number | ((item: T) => number);
	renderItem: (v: T, index: number) => React.ReactNode;
	itemsGap?: number;
	overItemsCount?: number;
	/** Use an existing scroll container, e.g. a menu with several grouped lists. */
	scrollContainerRef?: React.RefObject<HTMLDivElement>;
	activeIndex?: number;
	itemKey?: (item: T) => React.Key;
};

export const VirtualizedList = <T,>({
	itemHeight = 80,
	items,
	renderItem,
	className,
	itemsGap = 10,
	overItemsCount = 10,
	scrollContainerRef,
	activeIndex,
	itemKey,
}: React.PropsWithChildren<Props<T>>) => {
	const contentWrapRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const [viewport, setViewport] = useState({ top: 0, height: 0 });
	const layout = useMemo(() => getListLayout(items, itemHeight, itemsGap), [items, itemHeight, itemsGap]);

	useEffect(() => {
		const container = scrollContainerRef ? scrollContainerRef.current : contentWrapRef.current;
		const list = listRef.current;
		if (!container || !list) return undefined;
		const row = activeIndex === undefined ? undefined : layout.rows[activeIndex];
		if (row) {
			const top =
				list.getBoundingClientRect().top -
				container.getBoundingClientRect().top -
				container.clientTop +
				row.top;
			if (top < 0) container.scrollTop += top;
			else if (top + row.height > container.clientHeight)
				container.scrollTop += top + row.height - container.clientHeight;
		} else if (!scrollContainerRef) {
			container.scrollTop = 0;
		}
		const measure = () => {
			const top = container.getBoundingClientRect().top + container.clientTop - list.getBoundingClientRect().top;
			const height = container.clientHeight;
			setViewport(current => (current.top === top && current.height === height ? current : { top, height }));
		};
		measure();
		container.addEventListener('scroll', measure);
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		observer.observe(list);
		return () => {
			container.removeEventListener('scroll', measure);
			observer.disconnect();
		};
	}, [activeIndex, scrollContainerRef, layout]);

	const { start, end } = getVisibleRange(layout.rows, viewport.top, viewport.height, overItemsCount);
	const indices = Array.from({ length: end - start }, (_, index) => start + index);
	// Keep aria-activedescendant valid when the user scrolls the selected option out of view.
	if (activeIndex !== undefined && layout.rows[activeIndex] && (activeIndex < start || activeIndex >= end)) {
		indices.push(activeIndex);
		indices.sort((a, b) => a - b);
	}

	return (
		<div className={cn(!scrollContainerRef && css.listContent, className)} ref={contentWrapRef}>
			<div ref={listRef} className={css.list} style={{ height: layout.height }}>
				{indices.map(index => (
					<div
						key={itemKey ? itemKey(items[index]) : index}
						style={{ ...layout.rows[index], left: 0, position: 'absolute' }}
						className={css.itemWrapper}
					>
						{renderItem(items[index], index)}
					</div>
				))}
			</div>
		</div>
	);
};
